import { getAllFilterParams, getPartnersAllowlist } from 'extractFromState';
import { getAllTicketBaseQueryFilter } from '../../api/tickets/queries';
import { liveQuerySubscribeLock, LIVE_QUERY_SUBSCRIBE_LOCK_KEY } from 'util/asyncLocks';
import { updateTicketInQueue } from 'actions/ticketActions';
import { isSpecialistLite, getWhiteList, extractSelectedInboxTab } from 'extractFromState';
import { isConversationInDateRange } from './helpers/conversationDateFilter';
import { isConversationMatchingIdentifiers } from './helpers/conversationIdentifierFilter';
import { addTicketToQueue, removeTicketFromQueue, selectTicket } from 'actions';
import * as tabs from 'constants/tabNames';
import {
  CONVERSATION_CREATE_SUBSCRIPTION,
  CONVERSATION_ENTER_SUBSCRIPTION,
  CONVERSATION_UPDATE_SUBSCRIPTION,
  CONVERSATION_LEAVE_SUBSCRIPTION,
} from './conversationSubscriptions.gql';
import subscribeToLiveQuery from '../subscribeToLiveQuery';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

let allQueueSubscriptions = [];
let filterParams = null;

const getAllQueryFilters = (state) => {
  const {
    ticketStatus,
    businessIdList,
    brandOptions,
    excludeBusinesses,
    simplrTicketNumber,
    widgetId,
    roomId,
    platformTicketId,
    dateRange,
  } = getAllFilterParams(state);

  const partnersAllowlist = getPartnersAllowlist(state);

  const filter = getAllTicketBaseQueryFilter({
    ticketStatus,
    businessIdList,
    brandOptions,
    excludeBusinesses,
    dateRange,
    simplrTicketNumber,
    widgetId,
    roomId,
    platformTicketId,
    partnersAllowlist,
  });

  return filter;
};

const startAllLiveQuery = (store) => {
  if (store.getState().user.isAdmin) {
    let state = store.getState();
    let selectedInboxTab = state.ui.messages.selectedInboxTab;
    const { dateRange, simplrTicketNumber, widgetId, roomId, platformTicketId, partialMatch } =
      getAllFilterParams(state);

    const variables = {
      filter: getAllQueryFilters(state),
    };

    /*
     *  Live Query will be started only when:
     *      1) it wasn't started before
     *                  Or
     *      2) Specialist wants to see the ticket that is in status other than currently subscribed Status
     *                  Or
     *      3) Specialist wants to see the ticket of an Expert that is other than currently subscribed Expert
     */
    if (!allQueueSubscriptions.length || (allQueueSubscriptions.length && !isEqual(filterParams, variables))) {
      if (allQueueSubscriptions.length) {
        allQueueSubscriptions.forEach((s) => s?.unsubscribe());
        allQueueSubscriptions = [];
        filterParams = null;
      }

      liveQuerySubscribeLock.acquire(LIVE_QUERY_SUBSCRIBE_LOCK_KEY, async (lockDoneFunction) => {
        const identifierFilters = { simplrTicketNumber, widgetId, roomId, platformTicketId };

        // NOTE(vlad|SPD-14091:2026-02-05): LiveQuery doesn't respect date/identifier filters, so we filter manually
        const isConversationPassingManualFilters = (conversationObj) =>
          isConversationInDateRange(conversationObj, dateRange) &&
          isConversationMatchingIdentifiers(conversationObj, identifierFilters, partialMatch);

        const handleOnCreate = async (conversationObj) => {
          if (!isConversationPassingManualFilters(conversationObj)) {
            return;
          }

          /*
           *  This event will be triggered when a ticket meets the Specialist's currently selected All ticket filter criterias
           *
           *  Note: There is no need as such to delay the execution of this event as it can not lead to duplicate action dispatch
           *  because of some UI action taken by Specialist
           */
          let allTickets = store.getState().tickets.allTickets;
          if (!allTickets[conversationObj.objectId]) {
            store.dispatch(
              addTicketToQueue({
                conversation: conversationObj,
                queueName: tabs.ALL,
              })
            );
            allTickets = store.getState().tickets.allTickets;
            selectedInboxTab = store.getState().ui.messages.selectedInboxTab;

            if (allTickets.allTickets.length === 1 && selectedInboxTab === tabs.ALL) {
              store.dispatch(selectTicket(allTickets.allTickets[0], tabs.ALL));
            }
          }
        };

        const handleOnEnter = async (conversationObj) => {
          // don't update queue if in a different tab
          if (store.getState().ui.messages.selectedInboxTab !== tabs.ALL) {
            // skip "All" tab live query event for conversation
            return;
          }

          if (!isConversationPassingManualFilters(conversationObj)) {
            return;
          }

          // do not update redux store if specialist lite and ticket not in whitelist
          const userIsSpecialistLite = isSpecialistLite(store.getState());
          const whitelist = getWhiteList(store.getState());
          if (
            userIsSpecialistLite &&
            whitelist &&
            conversationObj.simplrAccountId &&
            !whitelist.includes(conversationObj.simplrAccountId)
          ) {
            return;
          }

          const partnersAllowlist = getPartnersAllowlist(store.getState());
          const isConversationExcludedByPartnersAllowlist =
            !isEmpty(partnersAllowlist) &&
            conversationObj.simplrAccountId &&
            !partnersAllowlist.includes(conversationObj.simplrAccountId);
          if (isConversationExcludedByPartnersAllowlist) {
            return;
          }

          let allTickets = store.getState().tickets.allTickets;
          if (!allTickets[conversationObj.objectId]) {
            await store.dispatch(
              addTicketToQueue({
                conversation: conversationObj,
                queueName: tabs.ALL,
              })
            );

            allTickets = store.getState().tickets.allTickets;
            selectedInboxTab = extractSelectedInboxTab(store.getState());
            if (allTickets.allTickets.length === 1 && selectedInboxTab === tabs.ALL) {
              await store.dispatch(selectTicket(allTickets.allTickets[0], tabs.ALL));
            }
          }
        };

        const handleOnLeave = (conversationObj) => {
          /*
           *  This event will be triggered when a Ticket doesn't fall under currently selected ticket status or belongs to currently selected expert
           */
          // Ticket count total will be updated instantly without the delay imposed on other actions for forwardConversationToExpert()
          let allTickets = store.getState().tickets.allTickets;

          allTickets = store.getState().tickets.allTickets;
          if (allTickets[conversationObj.objectId]) {
            store.dispatch(removeTicketFromQueue(conversationObj.objectId, tabs.ALL, store.getState().user.isAdmin));

            let selectedTicketId = store.getState().selectedTicketId;
            selectedInboxTab = store.getState().ui.messages.selectedInboxTab;
            if (selectedTicketId === conversationObj.objectId && selectedInboxTab === tabs.ALL) {
              const newSelectedTicketId = store.getState().tickets.allTickets.allTickets[0];
              store.dispatch(selectTicket(newSelectedTicketId, tabs.ALL));
            }
          }
        };

        const handleOnUpdate = (conversationObj) => {
          /*
            * This event will be triggered when there is an update to a ticket that does not change the ticket's inclusion in the filter

            * When the ticket status filter is set to all instead of removing a ticket from the queue we will update the ticket status
            */
          if (selectedInboxTab === tabs.ALL) {
            store.dispatch(updateTicketInQueue(conversationObj, selectedInboxTab, store.getState().user.isAdmin));
          }
        };

        const createSubscription = subscribeToLiveQuery(
          CONVERSATION_CREATE_SUBSCRIPTION,
          variables,
          'onCreateConversation',
          handleOnCreate
        );

        const updateSubscription = subscribeToLiveQuery(
          CONVERSATION_UPDATE_SUBSCRIPTION,
          variables,
          'onUpdateConversation',
          handleOnUpdate
        );

        const enterSubscription = subscribeToLiveQuery(
          CONVERSATION_ENTER_SUBSCRIPTION,
          variables,
          'onEnterConversation',
          handleOnEnter
        );

        const leaveSubscription = subscribeToLiveQuery(
          CONVERSATION_LEAVE_SUBSCRIPTION,
          variables,
          'onLeaveConversation',
          handleOnLeave
        );

        allQueueSubscriptions.push(createSubscription, updateSubscription, enterSubscription, leaveSubscription);
        lockDoneFunction();
      });
    }
  }
};

export { startAllLiveQuery };
